// src/utils/useRequestsLifecycles.js
//
// Slot lifecycles for the Raw requests tab (headline, slot-referenced rows, billing
// merge). Same rule as the Slots and Lifecycle tabs: the proxy's answer
// (postLifecycleGroups, newest-arrived-answer-wins) whenever it responds, the
// local buildSlotLifecycles twin only until then / when the proxy is down.
// Disabled (returns []) outside the Raw requests view so other tabs pay nothing.
import { useEffect, useMemo, useState } from 'react';
import { postLifecycleGroups } from '../api/lifecycle';
import { useLatestWins } from './latestWins';
import { buildSlotLifecycles } from '../components/AdLifecycleView';
import useCoalescedPost from './useCoalescedPost';

export default function useRequestsLifecycles(allRows, enabled) {
  const [serverGraph, setServerGraph] = useState(null);
  const [source, setSource] = useState('pending');
  const latest = useLatestWins();

  const active = !!(enabled && allRows && allRows.length);
  useEffect(() => {
    if (active) return;
    latest.invalidate();
    setServerGraph(null);
    setSource('pending');
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps
  // One POST in flight at a time, at most every few seconds (utils/useCoalescedPost.js).
  useCoalescedPost(() => {
      const seq = latest.begin();
      return postLifecycleGroups(allRows, { manualOverrides: {} })
        .then((res) => {
          if (!latest.accept(seq)) return;
          setServerGraph(res);
          setSource('server');
        })
        .catch(() => {
          if (!latest.accept(seq)) return;
          setServerGraph(null);
          setSource('local');
        });
  }, [allRows], { enabled: active });

  return useMemo(() => {
    if (!enabled || !allRows || !allRows.length) return [];
    if (source === 'server' && serverGraph && Array.isArray(serverGraph.lifecycles)) return serverGraph.lifecycles;
    try {
      return buildSlotLifecycles(allRows, {});
    } catch {
      return [];
    }
  }, [enabled, allRows, source, serverGraph]);
}
